import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Dialog } from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { useCreateEmployee, useUpdateEmployee } from '@/hooks/useHr';
import { extractError } from '@/api/client';
import { useAuthStore } from '@/stores/authStore';
import {
  CONTRACT_TYPE_LABELS,
  EMPLOYEE_STATUS_LABELS,
  type ContractType,
  type Employee,
  type EmployeeStatus,
} from '@/types';

interface Props {
  open:      boolean;
  onClose:   () => void;
  employee?: Employee;
}

interface FormState {
  matricule:    string;
  fullName:     string;
  email:        string;
  phone:        string;
  department:   string;
  jobTitle:     string;
  contractType: ContractType;
  hireDate:     string;
  salaryFcfa:   string;
  status:       EmployeeStatus;
  // PAIE-1 (19/10/2026) : identité utile aux attestations et certificats
  civilite:      '' | 'M.' | 'Mme';
  dateNaissance: string;
  lieuNaissance: string;
  numeroCnps:    string;
  endDate:       string;
  // PAIE-2 : dossier de paie ; virement : DAF et PDG seulement
  situationFamiliale:  '' | 'celibataire' | 'marie' | 'divorce' | 'veuf';
  enfantsACharge:      string;
  categorieConvention: string;
  soumisCnps:          boolean;
  dateAnciennete:      string;
  banqueNom:           string;
  compteIntitule:      string;
  compteNumero:        string;
}

const todayISO = () => new Date().toISOString().slice(0, 10);

const emptyForm = (): FormState => ({
  matricule: '', fullName: '', email: '', phone: '',
  department: '', jobTitle: '', contractType: 'CDI',
  hireDate: todayISO(), salaryFcfa: '', status: 'active',
  civilite: '', dateNaissance: '', lieuNaissance: '', numeroCnps: '', endDate: '',
  situationFamiliale: '', enfantsACharge: '', categorieConvention: '', soumisCnps: true, dateAnciennete: '', banqueNom: '', compteIntitule: '', compteNumero: '',
});

const toForm = (e: Employee): FormState => ({
  matricule:    e.matricule,
  fullName:     e.fullName,
  email:        e.email ?? '',
  phone:        e.phone ?? '',
  department:   e.department,
  jobTitle:     e.jobTitle,
  contractType: e.contractType,
  hireDate:     e.hireDate.slice(0, 10),
  salaryFcfa:   e.salaryCents ? String(Number(BigInt(e.salaryCents)) / 100) : '',
  status:       e.status,
  civilite:      e.civilite ?? '',
  dateNaissance: e.dateNaissance?.slice(0, 10) ?? '',
  lieuNaissance: e.lieuNaissance ?? '',
  numeroCnps:    e.numeroCnps ?? '',
  endDate:       e.endDate?.slice(0, 10) ?? '',
  situationFamiliale:  e.situationFamiliale ?? '',
  enfantsACharge:      e.enfantsACharge != null ? String(e.enfantsACharge) : '',
  categorieConvention: e.categorieConvention ?? '',
  soumisCnps:          e.soumisCnps !== false,
  dateAnciennete:      e.dateAnciennete?.slice(0, 10) ?? '',
  banqueNom:           e.banqueNom ?? '',
  compteIntitule:      e.compteIntitule ?? '',
  compteNumero:        e.compteNumero ?? '',
});

export const EmployeeFormDialog = ({ open, onClose, employee }: Props) => {
  const isEdit = !!employee;
  const role = useAuthStore((st) => st.user?.role);
  const voitVirement = role === 'daf' || role === 'admin_holding';
  /** PAIE-2 : champs du dossier de paie, envoyés à la création comme à la modification. */
  const dossier = () => ({
    situationFamiliale:  form.situationFamiliale || undefined,
    enfantsACharge:      form.enfantsACharge !== '' ? Number(form.enfantsACharge) : undefined,
    categorieConvention: form.categorieConvention,
    soumisCnps:          form.soumisCnps,
    dateAnciennete:      form.dateAnciennete || undefined,
    ...(voitVirement ? { banqueNom: form.banqueNom, compteIntitule: form.compteIntitule, compteNumero: form.compteNumero } : {}),
  });
  const [form, setForm] = useState<FormState>(emptyForm());
  const [error, setError] = useState<string | null>(null);
  const create = useCreateEmployee();
  const update = useUpdateEmployee();

  useEffect(() => {
    if (open) {
      setForm(employee ? toForm(employee) : emptyForm());
      setError(null);
    }
  }, [open, employee]);

  const set = <K extends keyof FormState>(k: K, v: FormState[K]) =>
    setForm((prev) => ({ ...prev, [k]: v }));

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      if (isEdit && employee) {
        // PATCH ne supporte que certains champs (cf. UpdateEmployeeDto backend)
        await update.mutateAsync({
          id: employee.id,
          payload: {
            department:  form.department.trim(),
            jobTitle:    form.jobTitle.trim(),
            phone:       form.phone.trim() || undefined,
            status:      form.status,
            salaryCents: form.salaryFcfa ? Math.round(parseFloat(form.salaryFcfa) * 100) : undefined,
            civilite:      form.civilite || undefined,
            dateNaissance: form.dateNaissance || undefined,
            lieuNaissance: form.lieuNaissance,
            numeroCnps:    form.numeroCnps,
            endDate:       form.endDate || null,
            ...dossier(),
          },
        });
      } else {
        await create.mutateAsync({
          matricule:    form.matricule.trim(),
          fullName:     form.fullName.trim(),
          email:        form.email.trim() || undefined,
          phone:        form.phone.trim() || undefined,
          department:   form.department.trim(),
          jobTitle:     form.jobTitle.trim(),
          contractType: form.contractType,
          hireDate:     form.hireDate,
          salaryCents:  form.salaryFcfa ? Math.round(parseFloat(form.salaryFcfa) * 100) : undefined,
          currency:     'FCFA',
          civilite:      form.civilite || undefined,
          dateNaissance: form.dateNaissance || undefined,
          lieuNaissance: form.lieuNaissance.trim() || undefined,
          numeroCnps:    form.numeroCnps.trim() || undefined,
          ...dossier(),
        });
      }
      onClose();
    } catch (err) {
      setError(extractError(err).message);
    }
  };

  const submitting = create.isPending || update.isPending;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={isEdit ? `Modifier — ${employee?.matricule}` : 'Nouvel employé'}
      subtitle={isEdit ? employee?.fullName : 'Création fiche personnel (DAF uniquement)'}
      size="lg"
    >
      <form onSubmit={onSubmit} className="space-y-3">
        {error && (
          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">{error}</div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <Field label="Matricule *">
            <Input value={form.matricule} onChange={(e) => set('matricule', e.target.value)} placeholder="LCI-2026-001" required disabled={isEdit} />
          </Field>
          <Field label="Statut">
            <select
              value={form.status}
              onChange={(e) => set('status', e.target.value as EmployeeStatus)}
              className="flex h-10 w-full rounded-md border border-gray-200 bg-white px-3 text-sm"
              disabled={!isEdit}
            >
              {(Object.keys(EMPLOYEE_STATUS_LABELS) as EmployeeStatus[]).map((s) => (
                <option key={s} value={s}>{EMPLOYEE_STATUS_LABELS[s]}</option>
              ))}
            </select>
          </Field>
        </div>

        <Field label="Nom complet *">
          <Input value={form.fullName} onChange={(e) => set('fullName', e.target.value)} required disabled={isEdit} />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Email">
            <Input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} disabled={isEdit} />
          </Field>
          <Field label="Téléphone">
            <Input value={form.phone} onChange={(e) => set('phone', e.target.value)} placeholder="+225..." />
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Département *">
            <Input value={form.department} onChange={(e) => set('department', e.target.value)} placeholder="Production" required />
          </Field>
          <Field label="Poste *">
            <Input value={form.jobTitle} onChange={(e) => set('jobTitle', e.target.value)} placeholder="Chef de chantier" required />
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Type de contrat *">
            <select
              value={form.contractType}
              onChange={(e) => set('contractType', e.target.value as ContractType)}
              className="flex h-10 w-full rounded-md border border-gray-200 bg-white px-3 text-sm"
              disabled={isEdit}
            >
              {(Object.keys(CONTRACT_TYPE_LABELS) as ContractType[]).map((t) => (
                <option key={t} value={t}>{CONTRACT_TYPE_LABELS[t]}</option>
              ))}
            </select>
          </Field>
          <Field label="Date d'embauche *">
            <Input type="date" value={form.hireDate} onChange={(e) => set('hireDate', e.target.value)} required disabled={isEdit} />
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Civilité (pour les attestations)">
            <select value={form.civilite} onChange={(e) => set('civilite', e.target.value as FormState['civilite'])}
              className="flex h-10 w-full rounded-md border border-gray-200 bg-white px-3 text-sm">
              <option value="">—</option><option value="M.">M.</option><option value="Mme">Mme</option>
            </select>
          </Field>
          <Field label="N° CNPS">
            <Input value={form.numeroCnps} onChange={(e) => set('numeroCnps', e.target.value)} />
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Date de naissance">
            <Input type="date" value={form.dateNaissance} onChange={(e) => set('dateNaissance', e.target.value)} />
          </Field>
          <Field label="Lieu de naissance">
            <Input value={form.lieuNaissance} onChange={(e) => set('lieuNaissance', e.target.value)} />
          </Field>
        </div>

        {isEdit && (
          <Field label="Date de sortie (pour le certificat de travail)">
            <Input type="date" value={form.endDate} min={form.hireDate} onChange={(e) => set('endDate', e.target.value)} />
          </Field>
        )}

        <div className="grid grid-cols-2 gap-3">
          <Field label="Situation de famille">
            <select value={form.situationFamiliale} onChange={(e) => set('situationFamiliale', e.target.value as FormState['situationFamiliale'])}
              className="flex h-10 w-full rounded-md border border-gray-200 bg-white px-3 text-sm">
              <option value="">—</option><option value="celibataire">Célibataire</option><option value="marie">Marié(e)</option>
              <option value="divorce">Divorcé(e)</option><option value="veuf">Veuf / veuve</option>
            </select>
          </Field>
          <Field label="Enfants à charge">
            <Input type="number" min="0" max="20" value={form.enfantsACharge} onChange={(e) => set('enfantsACharge', e.target.value)} />
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Catégorie (convention interprofessionnelle)">
            <Input value={form.categorieConvention} onChange={(e) => set('categorieConvention', e.target.value)} placeholder="ex. 4A" />
          </Field>
          <Field label="Ancienneté depuis (vide = embauche)">
            <Input type="date" value={form.dateAnciennete} onChange={(e) => set('dateAnciennete', e.target.value)} />
          </Field>
        </div>
        <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 12 }}>
          <input type="checkbox" checked={!form.soumisCnps} onChange={(e) => set('soumisCnps', !e.target.checked)} /> Non soumis à la CNPS (aucune cotisation CNPS sur ses bulletins)
        </label>

        {voitVirement && (
          <div className="grid grid-cols-3 gap-3">
            <Field label="Banque (virement)">
              <Input value={form.banqueNom} onChange={(e) => set('banqueNom', e.target.value)} />
            </Field>
            <Field label="Intitulé du compte">
              <Input value={form.compteIntitule} onChange={(e) => set('compteIntitule', e.target.value)} />
            </Field>
            <Field label="N° de compte (RIB)">
              <Input value={form.compteNumero} onChange={(e) => set('compteNumero', e.target.value)} />
            </Field>
          </div>
        )}

        <Field label="Salaire (FCFA / mois) — DAF uniquement">
          <Input type="number" min="0" step="1000" value={form.salaryFcfa} onChange={(e) => set('salaryFcfa', e.target.value)} placeholder="350000" />
        </Field>

        {isEdit && (
          <p className="rounded-md bg-blue-50 px-3 py-2 text-[11px] text-blue-700">
            En édition : matricule, nom, email, contrat et date d’embauche restent figés ; le reste se modifie.
          </p>
        )}

        <div className="flex justify-end gap-2 border-t border-gray-100 pt-3">
          <Button type="button" variant="secondary" onClick={onClose}>Annuler</Button>
          <Button type="submit" loading={submitting}>{isEdit ? 'Enregistrer' : 'Créer l\'employé'}</Button>
        </div>
      </form>
    </Dialog>
  );
};

const Field = ({ label, children }: { label: string; children: ReactNode }) => (
  <div>
    <label className="mb-1 block text-xs font-medium text-gray-600">{label}</label>
    {children}
  </div>
);
